import { useCallback, useState } from 'react';
import { toast } from 'sonner';

export interface UseCopyToClipboardOptions {
  /**
   * Accepted for backward compatibility with existing callers. Toast duration is
   * now controlled globally by the Toaster, so this value is no longer read.
   */
  snackbarDuration?: number;
  /** Custom success message (default: "Copied to clipboard") */
  successMessage?: string;
  /** Callback on successful copy */
  onSuccess?: () => void;
  /** Callback on copy error */
  onError?: (error: Error) => void;
}

export interface UseCopyToClipboardReturn {
  /** Copy text to clipboard */
  copy: (text: string) => Promise<boolean>;
  /** Whether copy operation is in progress */
  isCopying: boolean;
}

export function useCopyToClipboard(
  options: UseCopyToClipboardOptions = {}
): UseCopyToClipboardReturn {
  const { successMessage = 'Copied to clipboard', onSuccess, onError } = options;

  const [isCopying, setIsCopying] = useState<boolean>(false);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      if (!text || isCopying) return false;

      setIsCopying(true);
      try {
        await navigator.clipboard.writeText(text);
        toast(successMessage);
        onSuccess?.();
        return true;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Copy failed');
        toast.error(error.message);
        onError?.(error);
        return false;
      } finally {
        setIsCopying(false);
      }
    },
    [isCopying, successMessage, onSuccess, onError]
  );

  return {
    copy,
    isCopying,
  };
}
